/**
 * Gift With Purchase (GWP) promotion management.
 * Handles displaying GWP offers based on cart total thresholds,
 * managing the GWP product in cart, and popup interactions.
 * 
 * @description This module provides:
 * - Threshold-based GWP trigger detection
 * - Popup display for GWP selection
 * - Adding/removing GWP products from cart
 * - Claim button visibility management
 * - Variant selection in popup
 * 
 * @requires jQuery (for cart item removal event)
 * @requires compareArrays (global function)
 * 
 * @example
 * // Automatically initializes on page load
 * GWP.init();
 */
let cartChangedTimeout = null;

/**
 * Main GWP (Gift With Purchase) object managing promotional offers.
 * @namespace GWP
 */
let GWP = {
  /** @type {boolean} Prevents concurrent execution */
  running: false,
  /** @type {boolean} Tracks if popup is currently open */
  GWPPopupOpen: false,
  /** @type {string|null} Product handle for the GWP product */
  giftProduct: null,
  /** @type {Object|null} Full product data including variants */
  giftProductData: null,

  /**
   * Initializes the GWP module, sets up gift product data and event listeners.
   * 
   * @returns {void}
   * @sideEffect Sets up product data, binds click/events, fetches cart
   */
  init: function () {
    let _this = this;
    
    // Load gift product information from global settings
    this.giftProduct = window.gwpSettings.gift_product;
    this.giftProductData = window.gwpSettings.gift_product_data;
    this.claimButton = document.querySelector('.gwp-bar__claim')

    if (!this.running) {
      _this.running = true;
      // Check current cart for GWP product
      _this.fetchCartAndCheckForGWPProduct();

      // Bind "Add to Cart" button in GWP popup
      if(document.querySelector('.gwp-atc__button')) {
        document.querySelector('.gwp-atc__button').addEventListener("click", function (e) {
          _this.addGWPProductToCart();
        });
      }
      
      // Bind popup close buttons
      document.querySelectorAll('.gwp-popup__container .close, .gwp-popup__container .gwp-atc__cancel').forEach(function (el) {
        el.addEventListener("click", function (e) {
          _this.closeGWPPopup();
        });
      });
      
      // Bind variant select change to update image
      document.querySelectorAll('.gwp-popup__container select').forEach(function (el) {
        el.addEventListener("change", function (e) {
          _this.updateVariantImage();
        });
      });

      // Track when GWP item is removed from cart
      $(document).on('click', '.cart-item.gwp-gift-item .cart-item__remove', (e) => {
        localStorage.setItem('gwp-gift-item-removed-from-cart', 'true');
      });

      // Bind claim button to open popup
      if (_this.claimButton) _this.claimButton.addEventListener("click", function (e) {
        // Clear previous state and open popup
        localStorage.removeItem('gwp-gift-item-removed-from-cart');
        _this.openGWPPopup(true);
      });
    }
  },

  /**
   * Fetches current cart data and triggers GWP check.
   * 
   * @returns {void}
   * @sideEffect Fetches cart, calls checkCartForGWPProduct
   */
  fetchCartAndCheckForGWPProduct: function() {
    let _this = this;
    fetch('/cart.js').then(res => res.json()).then(response => {
      _this.checkCartForGWPProduct(response);
    });
  },

  /**
   * Checks cart contents against GWP thresholds and triggers appropriate actions.
   * Determines if GWP should be in cart, added, or removed.
   * 
   * @param {Object} data - Cart data from /cart.js
   * @returns {void}
   * @sideEffect May add GWP product, open popup, toggle claim button, remove GWP
   * 
   * @example
   * GWP.checkCartForGWPProduct(cartData);
   */
  checkCartForGWPProduct: function (data) {
    // Skip empty carts
    if (data.item_count < 1 || typeof data.items == "undefined") {
      localStorage.removeItem('gwp-trigger-in-cart');
      localStorage.removeItem('gwp-threshold-reached');
      this.running = false;
      this.toggleClaimButton();
      return;
    }

    let items = data.items;
    let _this = this;
    
    // Load threshold configuration
    let threshold = window.gwpSettings.threshold;
    let triggerOnAll = window.gwpSettings.trigger_on_all_products;
    let triggerProduct = window.gwpSettings.trigger_product;

    // Get minimum cart items count configuration
    let cartItemsCount = window.gwpSettings.cart_items;
    let GWPInCart = false;
    let GWPItemKey = null;
    // Determine if we should trigger on all products or specific product
    let triggerInCart = triggerOnAll || triggerProduct.length < 1;
    let totalPrice = 0;

    // Process each cart item
    items.forEach(function (item) {
      // Check if GWP product is already in cart
      if (item.properties.hasOwnProperty('_gwp_product')) {
        GWPInCart = true;
        GWPItemKey = item.key;
      }

      // Check if trigger product is in cart (if configured)
      if (triggerProduct != '') {
        if (item.product_id == parseInt(triggerProduct)) {
          triggerInCart = true;
          totalPrice += item.price * item.quantity
        }
      }
    });

    // Determine if we should trigger based on cart contents
    if (triggerOnAll) {
      totalPrice = data.total_price;
      localStorage.setItem('gwp-trigger-in-cart', 'true');
    } else if (triggerInCart) {
      localStorage.setItem('gwp-trigger-in-cart', 'true');
    } else {
      localStorage.removeItem('gwp-trigger-in-cart');
    }

    // Check if threshold is reached
    let thresholdReached = threshold <= totalPrice;

    // Store threshold state
    if (thresholdReached) {
      localStorage.setItem('gwp-threshold-reached', 'true');
    } else {
      localStorage.removeItem('gwp-threshold-reached');
    }

    // Add GWP product if threshold reached and trigger conditions met
    if (!GWPInCart && thresholdReached && triggerInCart) {
      // Show claim button instead of auto-adding
      if (document.querySelector('.gwp-bar__claim')) {
        document.querySelector('.gwp-bar__claim').classList.remove('hidden');
      }
      // Open popup to let user select variant
      _this.openGWPPopup();
    }

    // Handle GWP already in cart
    if(GWPInCart) {
      this.toggleClaimButton(true);

      // Remove GWP if threshold no longer reached
      // Special case: if only 1 item in cart and it's the GWP
      if (!thresholdReached && GWPItemKey || GWPInCart && !thresholdReached && cartItemsCount === 1 && GWPItemKey) {
        _this.updateItemQty(GWPItemKey, 0);
        // Remove discount element from DOM
        const gwpDomElement = document.querySelector('[data-discount-application-title="FreeGift"]');
        if (gwpDomElement) gwpDomElement.remove();
      }
    }
    else this.toggleClaimButton();
  },

  /**
   * Opens the GWP popup overlay.
   * Respects previous close state unless forced.
   * 
   * @param {boolean} [force=false] - Force open regardless of previous close state
   * @returns {void}
   * @sideEffect Adds CSS classes, may update overlay data attribute
   */
  openGWPPopup: function (force = false) {
    // Check if popup was previously closed (unless forced)
    if (localStorage.getItem('gwp-overlay-closed') != null && 
        localStorage.getItem('gwp-overlay-closed') === this.giftProduct && 
        !force) return;
    
    // Check if GWP was previously removed from cart (unless forced)
    if (localStorage.getItem('gwp-gift-item-removed-from-cart') != null && !force) return;

    let overlay = document.querySelector('.gwp-popup__overlay');
    overlay.setAttribute('data-handle', this.giftProduct);
    overlay.classList.add('active');

    document.querySelector('body').classList.add('noscroll');
    this.fixGWPOverlayImg();
    this.GWPPopupOpen = true;
  },

  /**
   * Closes the GWP popup overlay.
   * 
   * @param {boolean} [saveToSession=true] - Whether to remember closed state in localStorage
   * @returns {void}
   * @sideEffect Removes CSS classes, updates localStorage, toggles claim button
   */
  closeGWPPopup: function (saveToSession = true) {
    let overlay = document.querySelector('.gwp-popup__overlay');
    let handle = overlay.getAttribute('data-handle');
    overlay.classList.remove('active');
    document.querySelector('body').classList.remove('noscroll');

    // Remember closed state if requested
    if (saveToSession) localStorage.setItem('gwp-overlay-closed', handle);
    this.GWPPopupOpen = false;
    this.toggleClaimButton();
  },

  /**
   * Toggles visibility of the GWP claim button based on cart state.
   * 
   * @param {boolean} [hide=false] - Whether to force hide the button
   * @returns {void}
   * @sideEffect Modifies button display style
   */
  toggleClaimButton: function (hide=false) {
    // Get current state from localStorage
    let triggerInCart = localStorage.getItem('gwp-trigger-in-cart');
    let thresholdReached = localStorage.getItem('gwp-threshold-reached');
    let gwpClosed = localStorage.getItem('gwp-overlay-closed');
    let giftRemovedFromCart = localStorage.getItem('gwp-gift-item-removed-from-cart');

    // Get button element if not cached
    if (typeof this.claimButton == "undefined" && document.querySelector('.gwp-bar__claim')) {
      this.claimButton = document.querySelector('.gwp-bar__claim');
    }

    if (this.claimButton) {
      // Show button if threshold reached and (triggered OR (closed OR removed))
      if (!hide && thresholdReached) {
        if (triggerInCart && (gwpClosed || giftRemovedFromCart)) {
          this.claimButton.style.display = '';
        } else {
          this.claimButton.style.display = 'none';
        }
      } else {
        this.claimButton.style.display = 'none';
      }
    }
  },

  /**
   * Updates the displayed variant image in the GWP popup when options change.
   * 
   * @returns {void}
   * @sideEffect Updates img src attribute, shows/hides unavailable message
   */
  updateVariantImage: function () {
    let form = document.querySelector('form#gwp-form');
    let options = [];
    let currentVariant = null;
    let image = form.closest('.gwp-popup__container').querySelector('.image > img');

    // Build options array from form selects
    form.querySelectorAll('.product-form__item').forEach(function (item) {
      let select = item.querySelector('select');
      if (select != null) {
        options.push(select.value);
      }
    });

    // Find matching variant
    this.giftProductData.variants.forEach(function (variant) {
      if (compareArrays(variant.options, options)) {
        currentVariant = variant;
      }
    });

    // Update UI based on variant availability
    if (currentVariant != null) {
      document.querySelector('.gwp-message').classList.remove('active');
    } else {
      document.querySelector('.gwp-message').classList.add('active');
      this.running = false;
    }

    // Update image source
    if (currentVariant != null && currentVariant.featured_image != null) {
      image.setAttribute('src', currentVariant.featured_image.src);
    } else {
      image.setAttribute('src', this.giftProductData.featured_image);
    }
  },

  /**
   * Adjusts overlay image height based on viewport dimensions.
   * 
   * @returns {void}
   * @sideEffect Modifies element CSS (requires jQuery)
   */
  fixGWPOverlayImg: function () {
    let img = document.querySelector('.gwp-popup__container .text-image .image');
    let text = document.querySelector('.gwp-popup__container .text-image .text');
    // Tablet: match height to text
    if (window.innerWidth >= 768 && window.innerWidth < 1200) {
      if(text.length > 0) img.css('height', text[0].clientHeight);
    } 
    // Mobile: full height
    else if (window.innerWidth < 768) {
      img.css('height', '100%');
    }
  },

  /**
   * Adds the selected GWP variant to the cart with appropriate metadata.
   * 
   * @returns {void}
   * @sideEffect Makes API call to add product, dispatches events, may reload
   */
  addGWPProductToCart: function () {
    let form = document.querySelector('form#gwp-form');
    let options = [];
    let currentVariant = null;
    let _this = this;

    // Build options array from form
    form.querySelectorAll('.product-form__item').forEach(function (item) {
      let select = item.querySelector('select');
      if (select.length > 0) {
        options.push(select.value);
      }
    });

    // Find matching variant
    this.giftProductData.variants.forEach(function (variant) {
      if (compareArrays(variant.options, options)) {
        currentVariant = variant;
      }
    });

    // Fallback: try to get variant ID from hidden input
    if(!currentVariant) {
      let variantIdInput = form.querySelector('[name="gift-product-id"]');
      if(variantIdInput) {
        currentVariant = {
          id: variantIdInput.value,
          available: variantIdInput.dataset.available
        };
      }
    }

    // Add variant to cart if available
    if (null != currentVariant && currentVariant.available) {
      document.querySelector('.gwp-message').classList.remove('active');

      let data = {
        items: [{
          quantity: 1,
          id: currentVariant.id,
          properties: {
            '_gwp_product': 'true',
          }
        }]
      };

      fetch('/cart/add.js', {
        'method': 'POST',
        'headers': {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }).then(response => {
        // Notify cart changed
        document.dispatchEvent(new CustomEvent('theme:cartchanged', {bubbles: false, cancelable: false}));
        // Open cart drawer
        document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', {bubbles: true, cancelable: false}));
        
        // Reload on cart/checkout pages
        if (document.querySelector('body').classList.contains('template-cart') || document.querySelector('body').classList.contains('shopify-checkout')) {
          location.reload();
        }
        _this.running = false;
        _this.GWPPopupOpen = false;
        _this.closeGWPPopup(false);
        _this.toggleClaimButton(true);
      });
    } else {
      // Show unavailable message
      document.querySelector('.gwp-message').classList.add('active');
      _this.running = false;
    }
  },

  /**
   * Updates quantity of a cart item (including removal when qty is 0).
   * 
   * @param {string} id - Cart item key
   * @param {number} [qty=1] - New quantity
   * @returns {void}
   * @sideEffect Makes API call, may reload page
   */
  updateItemQty: function (id, qty = 1) {
    let _this = this;
    fetch('/cart/change.js', {
      'method': 'POST',
      'headers': {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({id: id, quantity: qty})
    }).then(response => {
      if (document.querySelector('body').classList.contains('template-cart') || document.querySelector('body').classList.contains('shopify-checkout')) {
        location.reload();
      }
      _this.running = false;
    });
  }
}

/**
 * Compares two arrays for equality regardless of order.
 * Used to match product variants by option values.
 * 
 * @param {Array} a - First array
 * @param {Array} b - Second array
 * @returns {boolean} True if arrays contain same values with same frequencies
 * 
 * @example
 * compareArrays(['S', 'Blue'], ['Blue', 'S']); // Returns true
 */
function compareArrays(a, b) {
  if (a.length !== b.length) return false;
  const uniqueValues = new Set([...a, ...b]);
  for (const v of uniqueValues) {
    const aCount = a.filter(e => e === v).length;
    const bCount = b.filter(e => e === v).length;
    if (aCount !== bCount) return false;
  }
  return true;
}

// Initialize GWP on page load
GWP.init();

// Listen for cart updates
document.addEventListener("cart:updated", (e) => {
  GWP.fetchCartAndCheckForGWPProduct();
});

// Debounced handler for cart changes
document.addEventListener('theme:cartchanged', (e) => {
  clearTimeout(cartChangedTimeout);
  cartChangedTimeout = setTimeout(function () {
    GWP.running = false;
    GWP.fetchCartAndCheckForGWPProduct();
  }, 250);
});
